import React, { useEffect, useMemo, useState } from "react";
import { useProjects } from "../context/ProjectContext";
import { useAuth } from "../context/AuthContext";
import api, { API, apiError } from "../lib/api";
import { NoProject, EmptyState } from "../components/EmptyState";
import { nf, pf, pct, signed, money } from "../lib/format";
import { statusMeta, pfColor, gainColor } from "../lib/status";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import CostCodeDetail from "../components/CostCodeDetail";
import { Loader2, Search, AlertTriangle, FileSpreadsheet, Pencil, Database, Undo2 } from "lucide-react";
import { toast } from "sonner";

// Columns in the exact order/grouping of the workbook Productivity tab.
// g=true marks the first column of a visual group (draws a left divider).
const COLS = [
  { k: "cost_code", label: "Cost Code", freeze: { left: 0, width: 112 }, fmt: (v) => v },
  { k: "description", label: "Description", freeze: { left: 112, width: 240 }, fmt: (v) => v },
  { k: "type_label", label: "Type", freeze: { left: 352, width: 56, last: true }, fmt: (v) => v || "" },
  { k: "uom", label: "UOM", fmt: (v) => v },
  { k: "standard_labor_rate", label: "Std $/hr", num: true, fmt: (v) => (v ? `$${nf(v, 0)}` : "—") },
  { k: "original_qty", label: "Orig Qty", num: true, g: true, fmt: (v) => nf(v, 0) },
  { k: "change_qty", label: "Chg Qty", num: true, fmt: (v) => nf(v, 0) },
  { k: "revised_qty", label: "Rev Qty", num: true, fmt: (v) => nf(v, 0) },
  { k: "original_hours", label: "Orig Hrs", num: true, g: true, fmt: (v) => nf(v) },
  { k: "change_hours", label: "Chg Hrs", num: true, fmt: (v) => nf(v) },
  { k: "revised_hours", label: "Rev Hrs", num: true, fmt: (v) => nf(v) },
  { k: "original_unit_rate", label: "Orig U/hr", num: true, g: true, fmt: (v) => nf(v, 2) },
  { k: "revised_unit_rate", label: "Rev U/Hr", num: true, fmt: (v) => nf(v, 2) },
  { k: "actual_unit_rate", label: "Actual U/hr", num: true, fmt: (v) => nf(v, 2) },
  { k: "installed_qty", label: "Procore Qty", num: true, g: true, fmt: (v) => nf(v, 0) },
  { k: "adjusted_qty", label: "Adjusted Qty", num: true, editable: true, field: "adjusted_qty", fmt: (v) => nf(v, 0) },
  { k: "total_qty", label: "Total Qty", num: true, fmt: (v) => nf(v, 0) },
  { k: "sage_hours", label: "Sage hrs", num: true, fmt: (v) => nf(v) },
  { k: "adjusted_hours", label: "Adjusted hrs", num: true, editable: true, field: "adjusted_hours", fmt: (v) => nf(v) },
  { k: "actual_hours", label: "Actual Hrs", num: true, fmt: (v) => nf(v) },
  { k: "remaining_qty", label: "Remain Qty", num: true, g: true, fmt: (v) => nf(v, 0) },
  { k: "remaining_hours", label: "Remain Hrs", num: true, fmt: (v) => nf(v) },
  { k: "percent_complete", label: "% Comp", num: true, fmt: (v) => pct(v) },
  { k: "earned_hours", label: "Earned Hrs", num: true, fmt: (v) => nf(v) },
  { k: "productivity_factor", label: "PF", num: true, pfc: true, fmt: (v) => pf(v) },
  { k: "forecast_hours", label: "Fcst Hrs", num: true, g: true, fmt: (v) => nf(v) },
  { k: "forecast_gain_loss", label: "Fcst hrs +/-", num: true, gain: true, fmt: (v) => signed(v) },
  { k: "forecast_dollar_gain_loss", label: "Fcst $+/-", num: true, gain: true, fmt: (v) => money(v) },
  { k: "jtd_hours_gain_loss", label: "JTD Hrs +/-", num: true, gain: true, g: true, fmt: (v) => signed(v) },
  { k: "jtd_dollar_gain_loss", label: "JTD $ +/-", num: true, gain: true, fmt: (v) => money(v) },
  { k: "status", label: "Status", g: true, fmt: (v) => v },
];

// OT lines carry only Installed Qty + Sage hrs; all other columns render blank.
const ROW_TINT = { "On Track": "rgba(236,253,245,0.4)", Watch: "rgba(255,251,235,0.4)", Unfavorable: "rgba(254,242,242,0.4)" };
const freezeStyle = (c) => (c.freeze ? { left: c.freeze.left, width: c.freeze.width, minWidth: c.freeze.width, maxWidth: c.freeze.width } : undefined);
const freezeCls = (c, bg) => (c.freeze ? `freeze ${bg} ${c.freeze.left ? "freeze-mid" : ""} ${c.freeze.last ? "freeze-last" : ""}` : "");
const OT_KEEP = new Set(["cost_code", "description", "type_label", "installed_qty", "sage_hours", "adjusted_qty", "adjusted_hours", "actual_hours"]);

// Footer total value per column key (blank if not summed in the workbook).
const TOTAL_MAP = {
  original_qty: "total_original_qty", change_qty: "total_change_qty", revised_qty: "total_revised_qty",
  original_hours: "total_original_hours", change_hours: "total_change_hours", revised_hours: "total_revised_hours",
  original_unit_rate: "total_original_unit_rate", revised_unit_rate: "total_revised_unit_rate",
  actual_unit_rate: "total_actual_unit_rate", installed_qty: "total_installed_qty",
  sage_hours: "total_sage_hours", adjusted_hours: "total_adjusted_hours", actual_hours: "total_actual_hours",
  remaining_qty: "total_remaining_qty", remaining_hours: "total_remaining_hours",
  percent_complete: "total_percent_complete", earned_hours: "total_earned_hours",
  productivity_factor: "overall_productivity_factor", forecast_hours: "total_forecast_hours",
  forecast_gain_loss: "total_forecast_gain_loss", forecast_dollar_gain_loss: "total_forecast_dollar_gain_loss",
  jtd_hours_gain_loss: "jtd_hours_gain_loss", jtd_dollar_gain_loss: "jtd_dollar_gain_loss",
};

function UomCell({ row, pid, onSaved }) {
  const [val, setVal] = useState(row.uom || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(row.uom || ""); }, [row.uom]);
  const commit = async () => {
    if ((val || "") === (row.uom || "")) return;
    setSaving(true);
    try {
      await api.patch(`/projects/${pid}/cost-codes/${row.id}/uom`, { uom: val });
      toast.success(`UOM updated for ${row.cost_code}`);
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setVal(row.uom || ""); }
    finally { setSaving(false); }
  };
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      data-testid={`uom-input-${row.line_id || row.cost_code}`}
      className={`w-14 font-mono bg-amber-50 border rounded px-1 py-0.5 outline-none focus:border-[#E8791A] ${saving ? "opacity-50" : "border-amber-300"}`}
    />
  );
}

function AdjustedCell({ row, pid, field, canEdit, onSaved }) {
  const endpoint = field === "adjusted_qty" ? "adjusted-qty" : "adjusted-hours";
  const decimals = field === "adjusted_qty" ? 0 : 1;
  const [val, setVal] = useState(String(row[field] ?? 0));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(String(row[field] ?? 0)); }, [row, field]);

  const commit = async () => {
    const num = parseFloat(val) || 0;
    if (num === (row[field] ?? 0)) return;
    setSaving(true);
    try {
      await api.patch(`/projects/${pid}/cost-codes/${row.id}/${endpoint}`, { [field]: num });
      toast.success(`Updated ${row.cost_code}`);
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setVal(String(row[field] ?? 0)); }
    finally { setSaving(false); }
  };

  if (!canEdit) {
    return <span className="font-mono" data-testid={`${endpoint}-readonly-${row.line_id || row.cost_code}`}>{nf(row[field], decimals)}</span>;
  }
  const tip = row[`${field}_by`]
    ? `Last edited by ${row[`${field}_by`]}${row[`${field}_at`] ? " on " + new Date(row[`${field}_at`]).toLocaleString() : ""}`
    : "Not yet edited";
  return (
    <input
      value={val}
      title={tip}
      onChange={(e) => setVal(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      inputMode="decimal"
      data-testid={`${endpoint}-input-${row.line_id || row.cost_code}`}
      className={`w-16 text-right font-mono bg-amber-50 border rounded px-1 py-0.5 outline-none focus:border-[#E8791A] ${saving ? "opacity-50" : "border-amber-300"}`}
    />
  );
}

export default function Report() {
  const { selected } = useProjects();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [uom, setUom] = useState("all");
  const [laborClass, setLaborClass] = useState("all");
  const [sortK, setSortK] = useState("cost_code");
  const [sortDir, setSortDir] = useState(1);
  const [detailCode, setDetailCode] = useState(null);

  const load = () => {
    if (!selected) { setLoading(false); return; }
    api.get(`/projects/${selected.id}/report`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { setLoading(true); load(); }, [selected]);

  const undoProcore = async () => {
    try {
      const { data: res } = await api.post(`/projects/${selected.id}/procore/budget-undo`);
      toast.success(`Undid last sync — restored ${res.restored} cost codes`); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const uoms = useMemo(() => [...new Set((data?.rows || []).map((r) => r.uom).filter(Boolean))], [data]);
  const classes = useMemo(() => [...new Set((data?.rows || []).map((r) => r.labor_class).filter(Boolean))], [data]);

  const rows = useMemo(() => {
    let r = data?.rows || [];
    if (q) r = r.filter((x) => `${x.cost_code} ${x.description}`.toLowerCase().includes(q.toLowerCase()));
    if (status !== "all") {
      if (status === "review") r = r.filter((x) => x.status.startsWith("Review") || x.status === "Duplicate Setup Code");
      else r = r.filter((x) => x.status === status);
    }
    if (uom !== "all") r = r.filter((x) => x.uom === uom);
    if (laborClass !== "all") r = r.filter((x) => x.labor_class === laborClass);
    r = [...r].sort((a, b) => {
      const av = a[sortK], bv = b[sortK];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number") return (av - bv) * sortDir;
      return String(av).localeCompare(String(bv)) * sortDir;
    });
    return r;
  }, [data, q, status, uom, laborClass, sortK, sortDir]);

  if (!selected) return <NoProject />;
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;
  if (!data || data.rows.length === 0)
    return <EmptyState title="No cost codes yet" hint="Add cost codes in Data Entry or import a budget." />;

  const t = data.totals;
  const pb = data.procore_budget || {};
  const sort = (k) => { if (sortK === k) setSortDir(-sortDir); else { setSortK(k); setSortDir(1); } };
  const dl = (path) => window.open(`${API}/projects/${selected.id}/export/${path}`, "_blank");
  const totalFmt = (c) => {
    const key = TOTAL_MAP[c.k];
    if (!key) return "";
    const v = t[key];
    return c.fmt(v);
  };

  return (
    <div className="space-y-4" data-testid="report-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold text-slate-900">Productivity</h1>
          {can.admin && (
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Pencil className="w-3 h-3 text-[#E8791A]" /> Adjusted hrs is editable inline — click a cell to change it.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => dl("exceptions.xlsx")} data-testid="export-exceptions"
                  className="hover:bg-slate-900 hover:text-white">
            <AlertTriangle className="w-4 h-4 mr-2" /> Exceptions
          </Button>
          <Button onClick={() => dl("report.xlsx")} data-testid="export-report-excel"
                  className="bg-[#0B192C] hover:bg-[#142843] text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Procore budget source */}
      {(pb.applied || pb.can_undo) && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="procore-budget-bar">
          {pb.applied && (
            <span className="inline-flex items-center gap-1 text-xs text-[#0B192C] bg-[#E8791A]/10 border border-[#E8791A]/30 px-2 py-1 rounded" data-testid="procore-source-label">
              <Database className="w-3.5 h-3.5 text-[#E8791A]" />
              Source: Procore, last synced {pb.synced_at ? new Date(pb.synced_at).toLocaleString() : "—"}
            </span>
          )}
          {can.admin && pb.can_undo && (
            <Button variant="outline" size="sm" onClick={undoProcore} data-testid="undo-sync-btn" className="ml-auto hover:bg-slate-900 hover:text-white">
              <Undo2 className="w-4 h-4 mr-1" /> Undo last sync
            </Button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input placeholder="Search cost code…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="pl-8 w-56 h-9" data-testid="report-search-input" />
        </div>
        <FilterSelect value={status} onChange={setStatus} testid="filter-status" label="Status"
          options={[["all", "All statuses"], ["On Track", "On Track"], ["Watch", "Watch"],
                    ["Unfavorable", "Unfavorable"], ["Not Started", "Not Started"], ["review", "Needs Review"]]} />
        <FilterSelect value={uom} onChange={setUom} testid="filter-uom" label="UOM"
          options={[["all", "All UOM"], ...uoms.map((u) => [u, u])]} />
        <FilterSelect value={laborClass} onChange={setLaborClass} testid="filter-class" label="Class"
          options={[["all", "All classes"], ...classes.map((c) => [c, c])]} />
        <span className="text-sm text-slate-500 ml-auto">{rows.length} of {data.rows.length} cost codes</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm">
        <div className="report-scroll">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100">
                {COLS.map((c) => (
                  <th key={c.k}
                      style={freezeStyle(c)}
                      onClick={() => sort(c.k)}
                      className={`px-2 py-2 text-slate-600 font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none 
                        ${c.num ? "text-right" : "text-left"} ${freezeCls(c, "bg-slate-100")} ${c.g ? "border-l-2 border-slate-300" : ""} ${c.w || ""}`}
                      data-testid={`col-${c.k}`}>
                    {c.editable && <Pencil className="w-3 h-3 inline mr-1 text-[#E8791A]" />}
                    {c.label}{sortK === c.k ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = statusMeta(r.status);
                const isOT = r.cost_type === "OT" || r.type_label === "OT";
                return (
                  <tr key={r.line_id || r.cost_code}
                      style={{ "--row-tint": ROW_TINT[r.status] }}
                      onClick={() => setDetailCode(r.cost_code)}
                      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${meta.row}`}
                      data-testid={`report-row-${r.line_id || r.cost_code}`}>
                    {COLS.map((c) => {
                      let cls = "px-2 py-1.5 whitespace-nowrap ";
                      cls += c.num ? "text-right font-mono " : "text-left ";
                      if (c.freeze) cls += freezeCls(c, "bg-white") + " ";
                      if (c.k === "cost_code") cls += "font-mono font-medium ";
                      if (isOT && !OT_KEEP.has(c.k)) {
                        if (c.g) cls += "border-l-2 border-slate-200 ";
                        return <td key={c.k} className={cls} data-testid={`ot-blank-${c.k}`} />;
                      }
                      if (c.pfc) cls += `font-semibold ${pfColor(r[c.k])} `;
                      if (c.gain) cls += gainColor(r[c.k]) + " ";
                      if (c.g) cls += "border-l-2 border-slate-200 ";
                      if (c.k === "uom" && can.manage) {
                        return (
                          <td key={c.k} className={cls}>
                            <UomCell row={r} pid={selected.id} onSaved={load} />
                          </td>
                        );
                      }
                      if (c.editable) {
                        return (
                          <td key={c.k} className={cls}>
                            <AdjustedCell row={r} pid={selected.id} field={c.field} canEdit={can.admin} onSaved={load} />
                          </td>
                        );
                      }
                      if (c.k === "description") {
                        return (
                          <td key={c.k} className={cls} style={freezeStyle(c)} title={r[c.k] || ""} data-testid={`desc-${r.line_id || r.cost_code}`}>
                            <div className="truncate">
                            {c.fmt(r[c.k])}
                            {r.not_in_procore && (
                              <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 align-middle" data-testid={`not-in-procore-${r.cost_code}`}>Not in Procore</span>
                            )}
                            </div>
                          </td>
                        );
                      }
                      if (c.k === "status") {
                        return (
                          <td key={c.k} className={cls + "border-l-2 border-slate-200"}>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.badge}`}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
                              {r.status}
                            </span>
                          </td>
                        );
                      }
                      return <td key={c.k} className={cls} style={freezeStyle(c)}>{c.fmt(r[c.k])}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#0B192C] text-white font-semibold sticky bottom-0" data-testid="report-totals-row">
                {COLS.map((c, i) => {
                  if (i === 0) return <td key={c.k} style={freezeStyle(c)} className={`px-2 py-2 ${freezeCls(c, "bg-[#0B192C]")}`}>TOTALS</td>;
                  if (i === 1) return <td key={c.k} style={freezeStyle(c)} className={`px-2 py-2 text-slate-300 text-[11px] ${freezeCls(c, "bg-[#0B192C]")}`}>Project roll-up</td>;
                  if (i === 2) return <td key={c.k} style={freezeStyle(c)} className={`px-2 py-2 ${freezeCls(c, "bg-[#0B192C]")}`} />;
                  const cls = `px-2 py-2 ${c.num ? "text-right font-mono" : ""} ${c.g ? "border-l-2 border-[#1E3E62]" : ""}`;
                  return <td key={c.k} className={cls}>{totalFmt(c)}</td>;
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {detailCode && (
        <CostCodeDetail projectId={selected.id} code={detailCode} onClose={() => setDetailCode(null)} />
      )}
    </div>
  );
}

const FilterSelect = ({ value, onChange, options, testid, label }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-40 text-sm" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);
